// Generated with util/create-component.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, InputNumber, DatePicker, Radio, Cascader, Button } from 'antd';
import type { GetProps } from 'antd';
import { PercentageOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { IPromoCodesFormProps } from './promo_codes_form.types';
import { IPromoCodeItem, IGiftCardStoreType, CascaderOption } from 'components/promo_codes/promo_codes.types';
import { parseAppliesTo, parseAppliesToForDefaultValue } from 'utils/promo_codes_cascader_builder';
import styles from './promo_codes_form.module.scss';
type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;

const requiredRules = [{ required: true }];
const { RangePicker } = DatePicker;

const PromoCodesForm: React.FC<IPromoCodesFormProps> = ({ promoCode, giftCardStoreTypes, onSave }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const codeTypeWatch = Form.useWatch('codeType', form);
  const isPercetageType = codeTypeWatch === 'percentage';
  const amountAddon = isPercetageType ? <PercentageOutlined /> : <DollarOutlined />;
  const inputRules = [
    { required: true },
    { min: 2, message: t('general.field.minimum_length', { length: 2 }) },
    { max: 50, message: t('general.field.max_length', { length: 50 }) },
  ];
  const disabledDate: RangePickerProps['disabledDate'] = current => {
    return current && current < dayjs().startOf('day');
  };

  useEffect(() => {
    if (promoCode?.validFrom && promoCode?.validTo) {
      form.setFieldValue('validationPeriod', [dayjs(promoCode.validFrom), dayjs(promoCode.validTo)]);
    }
    if (promoCode?.appliesTo) {
      const appliesTo = parseAppliesToForDefaultValue(promoCode.appliesTo);
      form.setFieldValue('applicableTo', appliesTo);
    }
    return () => {
      form.resetFields();
    };
  }, [promoCode]);

  useEffect(() => {
    if (giftCardStoreTypes) {
      const options: CascaderOption[] = giftCardStoreTypes.map((storeType: IGiftCardStoreType) => {
        return {
          value: storeType.storeId,
          label: storeType.storeName,
          children: storeType.giftCardTypes.map(giftCardType => {
            return {
              value: giftCardType.id,
              label: giftCardType.title,
            };
          }),
          disableCheckbox: storeType.giftCardTypes.length === 0,
        };
      });
      setCascaderOptions(options);
    }
  }, [giftCardStoreTypes]);
  const validateMessages = {
    required: t('validation_messages.required'),
  };
  const statusOptions = [
    { value: 'inactive', label: t('general.no') },
    { value: 'active', label: t('general.yes') },
  ];
  const discountTypeOptions = [
    { value: 'fixed_amount', label: t('rate_plan.modificator_option.decrease_by_amount') },
    { value: 'percentage', label: t('rate_plan.modificator_option.decrease_by_percent') },
  ];

  const onFinish = (values: IPromoCodeItem) => {
    setLoading(true);
    values.id = promoCode?.id;
    if (values.applicableTo) {
      values.appliesTo = parseAppliesTo(values.applicableTo, cascaderOptions);
      delete values.applicableTo;
    }
    if (!values.validationPeriod && (promoCode?.validFrom || promoCode?.validTo)) {
      values.validFrom = '';
      values.validTo = '';
    }
    if (values.validationPeriod) {
      values.validFrom = dayjs(values.validationPeriod[0]).toISOString();
      values.validTo = dayjs(values.validationPeriod[1]).toISOString();
      delete values.validationPeriod;
    }
    if (values.amount) {
      values.amount = Number(values.amount);
    }
    if (values.usageLimit) {
      values.usageLimit = Number(values.usageLimit);
    }
    try {
      onSave(values);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="PromoCodesForm" className={styles.root}>
      <Form form={form} initialValues={promoCode} validateMessages={validateMessages} onFinish={onFinish}>
        <Form.Item label={t('general.other.name')} name="name" rules={inputRules} data-testid="PromoCodesForm.Name">
          <Input placeholder={t('general.other.name')} />
        </Form.Item>
        <Form.Item label={t('promo_codes.promo_code')} name="code" rules={inputRules} data-testid="PromoCodesForm.Code">
          <Input placeholder={t('promo_codes.promo_code')} />
        </Form.Item>
        <Form.Item
          name="codeType"
          rules={requiredRules}
          label={t('promo_codes.discount_type')}
          data-testid="PromoCodesForm.CodeType"
        >
          <Radio.Group
            options={discountTypeOptions}
            optionType="button"
            buttonStyle="outline"
            data-testid="PromoCodesForm.DiscountTypeOptions"
          />
        </Form.Item>
        <Form.Item
          label={t('promo_codes.discount_amount')}
          name="amount"
          rules={requiredRules}
          data-testid="PromoCodesForm.Amount"
        >
          <InputNumber min={1} addonAfter={amountAddon} />
        </Form.Item>
        <Form.Item
          name="usageLimit"
          label={t('promo_code.usage_limit')}
          tooltip={t('promo_code.usage_limit.tooltip')}
          data-testid="PromoCodesForm.UsageLimit"
        >
          <InputNumber min={1} />
        </Form.Item>
        <Form.Item
          label={t('promo_codes.validation_period')}
          name="validationPeriod"
          data-testid="PromoCodesForm.ValidationPeriod"
        >
          <RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD" disabledDate={disabledDate} />
        </Form.Item>
        <Form.Item
          label={t('promo_codes.applies_to')}
          name="applicableTo"
          rules={requiredRules}
          data-testid="PromoCodesForm.ApplicableTo"
        >
          <Cascader options={cascaderOptions} multiple maxTagCount="responsive" />
        </Form.Item>
        <Form.Item
          name="status"
          rules={requiredRules}
          label={t('contracts.active')}
          data-testid="PromoCodesForm.Status"
        >
          <Radio.Group
            options={statusOptions}
            optionType="button"
            buttonStyle="outline"
            data-testid="PromoCodesForm.StatusOptions"
          />
        </Form.Item>
        <Form.Item>
          <Button
            data-testid="PromoCodesForm.Submit"
            className={styles.submitButton}
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            disabled={loading}
          >
            {t('general.buttons.save')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default PromoCodesForm;
